import { useEffect, useMemo, useState } from "react";
import { FiUserPlus, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { getStudents } from "../../../services/studentsApi";
import { assignStudentsToSubject, getSubjectById } from "../../../services/subjectsApi";
import TableEmptyState from "../../../components/common/tableEmptyState/TableEmptyState";
import styles from "./SubjectStudentsModal.module.css";
import { toast } from "react-toastify";

const SubjectStudentsModal = ({ isOpen, subject, onClose, onAssigned }) => {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [assignedStudentNumbers, setAssignedStudentNumbers] = useState([]);
  const [pendingStudentNumbers, setPendingStudentNumbers] = useState([]);

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setStudents([]);
      setAssignedStudentNumbers([]);
      setPendingStudentNumbers([]);
      setError("");
      setLoadingStudents(false);
      setLoadingAssigned(false);
      setSaving(false);
      return;
    }

    const fetchAllStudents = async () => {
      try {
        setLoadingStudents(true);
        const data = await getStudents({ pageNo: 1, pageSize: 1000 });
        const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
        setStudents(content);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message ||
            t("pages.subjectStudents.studentsLoadError")
        );
      } finally {
        setLoadingStudents(false);
      }
    };

    const fetchAssignedStudents = async () => {
      if (!subject?.id) return;
      try {
        setLoadingAssigned(true);
        const raw = await getSubjectById(subject.id);
        const data = raw?.data || raw;
        const assigned = Array.isArray(data?.students) ? data.students : [];
        const assignedNumbers = assigned.map((s) => s?.studentNumber).filter(Boolean);

        setAssignedStudentNumbers(assignedNumbers);
        setPendingStudentNumbers([]);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message ||
            t("pages.subjectStudents.assignedLoadError")
        );
      } finally {
        setLoadingAssigned(false);
      }
    };

    fetchAllStudents();
    fetchAssignedStudents();
  }, [isOpen, subject?.id]);

  const assignedSet = useMemo(() => new Set(assignedStudentNumbers), [assignedStudentNumbers]);
  const pendingSet = useMemo(() => new Set(pendingStudentNumbers), [pendingStudentNumbers]);

  const assignedStudents = useMemo(
    () => students.filter((s) => assignedSet.has(s.studentNumber)),
    [students, assignedSet]
  );

  const unassignedStudents = useMemo(
    () => students.filter((s) => !assignedSet.has(s.studentNumber)),
    [students, assignedSet]
  );

  const busy = loadingStudents || loadingAssigned;

  if (!isOpen || !subject) return null;

  const togglePending = (studentNumber) => {
    setPendingStudentNumbers((prev) =>
      prev.includes(studentNumber) ? prev.filter((n) => n !== studentNumber) : [...prev, studentNumber]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (pendingStudentNumbers.length === 0) {
      setError(t("pages.subjectStudents.pendingRequired"));
      return;
    }

    try {
      setSaving(true);
      await assignStudentsToSubject(subject.id, pendingStudentNumbers);

      toast.success(t("pages.subjectStudents.assignSuccess"));

      if (onAssigned) await onAssigned();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          t("pages.subjectStudents.assignError")
      );
    } finally {
      setSaving(false);
    }
  };

  const subjectName = subject.name || subject.title;

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="subject-students-modal-title">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <span className={styles.headerIcon}>
              <FiUserPlus size={22} />
            </span>
            <div className={styles.titleBlock}>
              <h2 id="subject-students-modal-title" className={styles.title}>
                {t("pages.subjectStudents.title")}
              </h2>
              {subjectName && <p className={styles.subtitle}>{t("pages.subjectStudents.subjectLabel", { name: subjectName })}</p>}
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("pages.subjectStudents.closeAria")}>
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formBody}>
            <div className={styles.studentsBox}>
              {busy && <p className={styles.infoText}>{t("common.loading")}</p>}

              {!busy && students.length === 0 && (
                <TableEmptyState
                  title={t("pages.subjectStudents.noStudentsTitle")}
                  description={t("pages.subjectStudents.noStudentsDescription")}
                />
              )}

              {!busy && students.length > 0 && (
                <div className={styles.listWrap}>
                  <div className={styles.listSection}>
                    <div className={styles.sectionHeader}>
                      <span className={styles.sectionTitle}>{t("pages.subjectStudents.assignedStudents")}</span>
                      <span className={styles.sectionCount}>{assignedStudents.length}</span>
                    </div>

                    {assignedStudents.length === 0 ? (
                      <p className={styles.infoText}>{t("pages.subjectStudents.noneAssigned")}</p>
                    ) : (
                      assignedStudents.map((st) => (
                        <div key={st.studentNumber} className={styles.studentRow}>
                          <span className={styles.studentName}>
                            {st.firstName} {st.lastName} — {st.studentNumber}
                          </span>
                        </div>
                      ))
                    )}
                  </div>

                  <div className={styles.divider} />

                  <div className={styles.listSection}>
                    <div className={styles.sectionHeader}>
                      <span className={styles.sectionTitle}>{t("pages.subjectStudents.unassignedStudents")}</span>
                      <span className={styles.sectionCount}>{unassignedStudents.length}</span>
                    </div>

                    {unassignedStudents.length === 0 ? (
                      <p className={styles.infoText}>{t("pages.subjectStudents.allAssigned")}</p>
                    ) : (
                      unassignedStudents.map((st) => (
                        <label key={st.studentNumber} className={styles.checkboxRow}>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={pendingSet.has(st.studentNumber)}
                            onChange={() => togglePending(st.studentNumber)}
                            disabled={saving}
                          />
                          <span className={styles.studentName}>
                            {st.firstName} {st.lastName} — {st.studentNumber}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className={styles.error} role="alert">
                {error}
              </div>
            )}
          </div>

          <div className={styles.actions}>
            <button type="button" onClick={onClose} className={styles.secondaryBtn} disabled={saving}>
              {t("common.close")}
            </button>
            <button type="submit" className={styles.primaryBtn} disabled={saving || busy || pendingStudentNumbers.length === 0}>
              {saving ? t("pages.subjectStudents.saving") : t("pages.subjectStudents.assign")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubjectStudentsModal;
